import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CampaignProvider } from '../../contexts/campaign';
import { MATERIAL_ICON_DOWNLOAD, MATERIAL_ICON_VIDEO } from '../../shared/common/Button/Icon';
import { FilterBar } from '../../shared/common/FilterBar';
import Pagination from '../../shared/common/Pagination';
import ProductList from '../../shared/common/ProductList';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import VideoPlayerPopup from '../../shared/components/VideoPlayerPopup';
import { BannerSlider } from '../../shared/slider/BannerSlider';
import ContentBaseLayout from '../../yourborrowbox/libx/shared/ContentBaseLayout';
import { CampaignListItem } from './CampaignListItem';
import { CampaignProductTile } from './CampaignProductTile';
import { Button, ButtonSize } from '../../shared/common/Button';
import { useParams } from 'react-router';
import { useBannersQuery } from '../useBannersQuery';
import { invalidateCampaignProducts, useCampaignProducts } from './api/useCampaignProducts';
import { ProductSorting } from '../../../models/Product';
import { getLoanFormat } from '../../shared/utils/getLoanFormat';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import { fetchCampaignProductsAggregationsQuickSearch } from '../../api/campaign-products/fetchCampaignProductsAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchCampaignProductsAggregations } from '../../api/campaign-products/fetchCampaignProductsAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { downloadCampaignGuide } from '../../api/marketing/your-campaigns/downloadCampaignGuide';
import { useTranslation } from 'react-i18next';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { useDisplayTypeSearchParam } from '../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../shared/utils/useDisplayFilterSearchParam';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../shared/filter/useProductFilters';
import { LANGUAGE_DE_DE } from '../../../i18n';

const StTrainingButtonContainer = styled.div`
    align-self: center;
    margin-bottom: 32px;

    * ~ * {
        margin-left: 8px;
    }
`;

const DEFAULT_PAGE_SIZE = 54;
// This is a temporary solution that we came up with Alex
// The product tile component styling is not styled correctly and not behaving as intened.
//We will find a better solution for the whole ProductTile component.
// https://borrowbox.atlassian.net/browse/PORTAL-4568
const StPagination = styled(Pagination)`
    margin-top: 200px;
`;

export const CampaignProductsPage: FC = () => {
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [urlParamHandled, setUrlParamHandled] = useState(false);

    const authentication = useAuthentication();
    const [searchParams] = useSearchParams();

    const { loanFormat: loanFormatPathParam, shopLanguage } = useParams<'loanFormat' | 'shopLanguage'>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    const { data: banners, isLoading: isBannersLoading } = useBannersQuery('CAMPAIGN_PRODUCTS', { shopLanguage });

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultSortBy: ProductSorting.RELEASE_DATE_OLD_TO_NEW,
        defaultPageSize: DEFAULT_PAGE_SIZE
    });

    const { productFilter } = useProductFilters();
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam();

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return [
                'campaignProductsPage',
                loanFormat.requestKey!,
                'aggregations',
                ageCategory,
                filters,
                expandedFilterGroups,
                shopLanguage
            ];
        },
        [ageCategory, loanFormat.requestKey, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchCampaignProductsAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory,
                shopLanguage
            }),
        [ageCategory, loanFormat.requestKey, shopLanguage]
    );

    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchCampaignProductsAggregationsQuickSearch({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory,
                shopLanguage
            }),
        [ageCategory, loanFormat.requestKey, shopLanguage]
    );
    //endregion

    const {
        list: products,
        totalHits,
        isLoading: isProductsLoading
    } = useCampaignProducts({
        filterMap: productFilter,
        loanFormat: loanFormat.requestKey!,
        ageCategory,
        pagination: {
            page,
            pageSize,
            sortBy
        },
        shopLanguage
    });

    const loading = (isProductsLoading || isBannersLoading) ?? false;

    useEffect(() => {
        if (!loading && !showIntroVideo) {
            if (searchParams.has('intro') && !urlParamHandled) {
                setShowIntroVideo(true);
                setUrlParamHandled(true);
            }
        }
    }, [loading, searchParams, showIntroVideo, urlParamHandled]);

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateCampaignProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    return (
        <>
            {isEmpty(banners) ? null : (
                <BannerSlider itemWidth={2000} itemHeight={300} showButtons={false} showDots={true} showShadows={false}>
                    {banners?.map((bannerItem, i) => {
                        if (bannerItem.targetUrl) {
                            return (
                                <Link to={bannerItem.targetUrl} key={i}>
                                    <img src={bannerItem.imageUrl} />
                                </Link>
                            );
                        } else {
                            return <img src={bannerItem.imageUrl} key={i} />;
                        }
                    })}
                </BannerSlider>
            )}
            <ContentBaseLayout
                topComponent={
                    <>
                        {language !== LANGUAGE_DE_DE ? (
                            <StTrainingButtonContainer>
                                <Button
                                    onClick={() => setShowIntroVideo(true)}
                                    text={t('shop.campaigns.howToRunACampaign')}
                                    icon={MATERIAL_ICON_VIDEO}
                                    size={ButtonSize.LARGE}
                                />
                                <Button
                                    onClick={() => downloadCampaignGuide()}
                                    text={t('shop.campaigns.campaignGuide')}
                                    icon={MATERIAL_ICON_DOWNLOAD}
                                    size={ButtonSize.LARGE}
                                />
                            </StTrainingButtonContainer>
                        ) : null}
                        <FilterBar
                            sortBy={sortBy}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            excludeSorting={['stock']}
                            totalProducts={totalHits}
                            pageSize={pageSize}
                            displayType={display}
                        />
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    products && (products.length ?? 0) > 0 && !loading ? (
                        <CampaignProvider value={{ preventCampaignBadge: true }}>
                            <div className="ca-main">
                                <div className="ca-main-inner">
                                    <ProductList
                                        displayType={display}
                                        products={products}
                                        onAddPrice={changePrice}
                                        loanFormatOverride={loanFormat}
                                    >
                                        {({ key, isListView, ...value }) =>
                                            isListView ? (
                                                <CampaignListItem {...value} key={key} shopLanguage={shopLanguage} />
                                            ) : (
                                                <CampaignProductTile {...value} key={key} shopLanguage={shopLanguage} />
                                            )
                                        }
                                    </ProductList>
                                    <StPagination totalItems={totalHits} pageSize={pageSize} />
                                </div>
                            </div>
                        </CampaignProvider>
                    ) : (
                        <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                    )
                }
                documentTitle={t('shop.campaigns.campaigns')}
                modalComponent={
                    <VideoPlayerPopup
                        showVideo={showIntroVideo && language !== LANGUAGE_DE_DE}
                        hideVideo={() => setShowIntroVideo(false)}
                        vimeoId={354627777}
                    />
                }
                loading={loading}
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
